import type { ComponentType } from "react";
import { ArrowLeft, RotateCcw, X } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Routes } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { ObsDiscardDraftDialog } from "@/features/obs/components/wizard/ObsDiscardDraftDialog";
import { ObsWizardFooter } from "@/features/obs/components/wizard/ObsWizardFooter";
import { ObsWizardProvider } from "@/features/obs/components/wizard/ObsWizardProvider";
import { ObsWizardSteps } from "@/features/obs/components/wizard/ObsWizardSteps";
import { StepCharts } from "@/features/obs/components/wizard/StepCharts";
import { StepFile } from "@/features/obs/components/wizard/StepFile";
import { StepIdentity } from "@/features/obs/components/wizard/StepIdentity";
import { StepMapping } from "@/features/obs/components/wizard/StepMapping";
import { StepPublish } from "@/features/obs/components/wizard/StepPublish";
import { StepUnits } from "@/features/obs/components/wizard/StepUnits";
import { useObsWizard } from "@/features/obs/hooks/obs-wizard-context";
import {
  isWizardStep,
  type WizardStep,
} from "@/features/obs/lib/wizard-steps";

/**
 * تقرير جديد — creating an OBS report, one step per URL.
 *
 * 🔑 THIS WAS A MODAL. Six steps, a file upload, a column-mapping table and a
 * live preview do not belong in a box that closes when the page behind it is
 * clicked — and the flow's most expensive input, the confirmed mapping, was the
 * easiest thing in the dashboard to lose. It is now a page of its own, opened in
 * its own tab from the reports list, with each step at its own address:
 *
 *     /obs-reports/new/identity   … /new/publish
 *
 * so the browser's back and forward buttons move between steps, a step can be
 * reloaded or linked to, and the draft is written to storage as it is typed
 * (see lib/draft-store). The bare `/new` resumes wherever the operator left off.
 *
 * WHAT DID NOT CHANGE: the order of the steps, what each one refuses to let past
 * it, and the fact that the report becomes real on the server at the units step.
 * Those rules moved from "the modal never let you skip" into explicit
 * reachability (lib/wizard-steps), which is what makes a typed URL safe.
 */

const LIST_PATH = appPath(Routes.OBS_REPORTS);

const STEP_VIEWS: Record<WizardStep, ComponentType> = {
  identity: StepIdentity,
  file: StepFile,
  mapping: StepMapping,
  units: StepUnits,
  charts: StepCharts,
  publish: StepPublish,
};

/** Renders whichever step the wizard resolved to. Used by every step route. */
export function ObsWizardStep() {
  const { step } = useObsWizard();
  const View = STEP_VIEWS[step];
  return <View />;
}

/**
 * Shown when a draft was found in storage — because silently restoring six steps
 * of somebody else's afternoon is worse than saying so. Carries the only way out
 * of a draft the operator no longer wants.
 */
function ResumeNotice() {
  const { resumed, dismissResumeNotice, startOver, draft } = useObsWizard();
  if (!resumed) return null;

  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-sky-500/40 bg-sky-500/5 p-3 text-sm">
      <p className="text-muted-foreground">
        استُؤنفت مسودة محفوظة
        {draft.titleAr.trim() ? ` لتقرير «${draft.titleAr.trim()}»` : ""} — كل ما
        أدخلته سابقًا ما زال في مكانه.
      </p>
      <div className="flex shrink-0 gap-2">
        <Button size="sm" variant="outline" onClick={startOver}>
          <RotateCcw className="size-4" />
          بدء تقرير جديد
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismissResumeNotice}
          aria-label="إخفاء التنبيه"
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}

/** The frame every step shares: the strip above it, the actions below it. */
function WizardFrame() {
  const { hydrated } = useObsWizard();

  // The draft's files are read back asynchronously, and which step is reachable
  // depends on them. Deciding before they land would bounce a restored draft to
  // step one and then correct itself — visibly.
  if (!hydrated) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <ResumeNotice />
      <ObsWizardSteps />

      <div className="rounded-xl border border-border bg-card p-4 md:p-6">
        <Outlet />
      </div>

      <ObsWizardFooter />
      <ObsDiscardDraftDialog />
    </div>
  );
}

export default function ObsCreateReportPage() {
  const { pathname } = useLocation();
  // The step is read off the path rather than from `useParams`, which in a layout
  // route sees only the params matched down to the layout itself — not the
  // child's `:step`.
  const tail = pathname.replace(/\/+$/, "").split("/").pop();
  const urlStep: WizardStep | null = isWizardStep(tail) ? tail : null;

  return (
    <ObsWizardProvider urlStep={urlStep}>
      <AdminPageShell
        title="تقرير جديد"
        description="ارفع ملف Excel، أكّد معنى أعمدته، ثم عاين الشاشة قبل نشرها. يُحفظ التعيين مع التقرير، فتُفحص كل الملفات اللاحقة تلقائيًا مقابله."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to={LIST_PATH}>
              <ArrowLeft className="size-4 rotate-180" />
              كل التقارير
            </Link>
          </Button>
        }
      >
        <WizardFrame />
      </AdminPageShell>
    </ObsWizardProvider>
  );
}
